import {
  VendorRiskResponseItem,
  VendorRiskResponseMappedBySection,
} from "@/modules/agent/types/vendorAssessment";
import { VendorCompanyDataItem } from "@/modules/agent/types/vendorCompanyData";
import { VendorRiskResponseItemForVendorOverview } from "@/modules/agent/types/vendorOverviewSection";
import { VendorRiskResponseItemForPotentialIssues } from "@/modules/agent/types/vendorPotentialIssues";
import { VendorRiskResponseItemForVendorSupplyChain } from "@/modules/agent/types/vendorSupplyChain";
import { create } from "zustand";

export type VendorPageType = "OVERVIEW" | "COMPANY" | "SUPPLY_CHAIN" | "DETAILED_REVIEW" | "POTENTIAL_ISSUES";

interface IVendorRiskAssessmentStore {
  selectedTab: VendorPageType;
  changesMade: number;
  isSaving: boolean;
  combinedVendorRiskData: VendorRiskResponseMappedBySection | null;
  vendorOverview: VendorRiskResponseItemForVendorOverview[];
  vendorCompanyData: VendorCompanyDataItem[];
  vendorSupplyChainData: VendorRiskResponseItemForVendorSupplyChain[];
  vendorAssessmentData: VendorRiskResponseItem[];
  vendorPotentialIssuesData: VendorRiskResponseItemForPotentialIssues[];
  setSelectedTab: (selectedTab: VendorPageType) => void;
  setCombinedVendorRiskData: (
    combinedVendorRiskData: VendorRiskResponseMappedBySection,
  ) => void;
  setVendorOverview: (
    vendorOverview: VendorRiskResponseItemForVendorOverview[],
    dontIncrementChangesMade?: boolean,
  ) => void;
  setVendorCompanyData: (
    vendorCompanyData: VendorCompanyDataItem[],
    dontIncrementChangesMade?: boolean,
  ) => void;
  setVendorSupplyChain: (
    vendorSupplyChainData: VendorRiskResponseItemForVendorSupplyChain[],
    dontIncrementChangesMade?: boolean,
  ) => void;
  setVendorAssessment: (
    vendorAssessmentData: VendorRiskResponseItem[],
    dontIncrementChangesMade?: boolean,
  ) => void;
  setVendorPotentialIssues: (
    vendorPotentialIssuesData: VendorRiskResponseItemForPotentialIssues[],
    dontIncrementChangesMade?: boolean,
  ) => void;
  setIsSaving: (newIsSaving: boolean) => void;
  reset: () => void;
}

export const useVendorRiskAssessmentStore = create<IVendorRiskAssessmentStore>(
  (set, get) => ({
    selectedTab: "OVERVIEW",
    changesMade: 0,
    isSaving: false,
    combinedVendorRiskData: null,
    vendorOverview: [],
    vendorCompanyData: [],
    vendorSupplyChainData: [],
    vendorAssessmentData: [],
    vendorPotentialIssuesData: [],
    setSelectedTab: (selectedTab: VendorPageType) => set({ selectedTab }),
    setCombinedVendorRiskData: (
      combinedVendorRiskData: VendorRiskResponseMappedBySection,
    ) => set({ combinedVendorRiskData }),
    setVendorOverview: (
      vendorOverview: VendorRiskResponseItemForVendorOverview[],
      dontIncrementChangesMade?: boolean,
    ) => {
      // Optimize: only update if data actually changed
      const currentData = get().vendorOverview;
      if (JSON.stringify(currentData) === JSON.stringify(vendorOverview)) {
        return;
      }
      
      set({
        vendorOverview,
        changesMade: get().changesMade + (dontIncrementChangesMade ? 0 : 1),
      });
    },
    setVendorCompanyData: (
      vendorCompanyData: VendorCompanyDataItem[],
      dontIncrementChangesMade?: boolean,
    ) => {
      set({
        vendorCompanyData,
        changesMade: get().changesMade + (dontIncrementChangesMade ? 0 : 1),
      });
    },
    setVendorSupplyChain: (
      vendorSupplyChainData: VendorRiskResponseItemForVendorSupplyChain[],
      dontIncrementChangesMade?: boolean,
    ) => {
      set({
        vendorSupplyChainData,
        changesMade: get().changesMade + (dontIncrementChangesMade ? 0 : 1),
      });
    },
    setVendorAssessment: (
      vendorAssessmentData: VendorRiskResponseItem[],
      dontIncrementChangesMade?: boolean,
    ) => {
      set({
        vendorAssessmentData,
        changesMade: get().changesMade + (dontIncrementChangesMade ? 0 : 1),
      });
    },
    setVendorPotentialIssues: (
      vendorPotentialIssuesData: VendorRiskResponseItemForPotentialIssues[],
      dontIncrementChangesMade?: boolean,
    ) => {
      set({
        vendorPotentialIssuesData,
        changesMade: get().changesMade + (dontIncrementChangesMade ? 0 : 1),
      });
    },
    setIsSaving: (newIsSaving: boolean) => set({ isSaving: newIsSaving }),
    reset: () => {
      set({
        selectedTab: "OVERVIEW",
        changesMade: 0,
        isSaving: false,
        combinedVendorRiskData: null,
        vendorOverview: [],
        vendorCompanyData: [],
        vendorSupplyChainData: [],
        vendorAssessmentData: [],
        vendorPotentialIssuesData: [],
      });
    },
  }),
);
